import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import { useNewsletterSubscribers } from '@/queries/newsletter';
import { useApiError } from '@/lib/useApiError';
import type {
  NewsletterSubscriber,
  NewsletterSubscribersFilters,
} from '@/schemas/admin/newsletter';

const searchSchema = z.object({
  locale: z.string().optional(),
  cohort: z.string().optional(),
  reveal: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const Route = createFileRoute('/_authed/newsletter/subscribers')({
  validateSearch: searchSchema,
  component: SubscribersScreen,
});

const LIMIT_OPTIONS = [25, 50, 100];

function SubscribersScreen() {
  const { t } = useTranslation('newsletter');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const reveal = search.reveal === '1';
  const filters: NewsletterSubscribersFilters = {
    ...(search.locale ? { locale: search.locale } : {}),
    ...(search.cohort ? { cohort: search.cohort } : {}),
    ...(reveal ? { reveal: true } : {}),
    ...(search.limit ? { limit: search.limit } : { limit: 25 }),
  };
  const list = useNewsletterSubscribers(filters);

  function setSearchPatch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({
      to: '/newsletter/subscribers',
      search: (prev) => {
        const next: Record<string, unknown> = { ...prev };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '') delete next[k];
          else next[k] = v;
        }
        return next;
      },
      replace: true,
    });
  }

  const columns: readonly DataColumn<NewsletterSubscriber>[] = [
    {
      id: 'email',
      header: t('subscribers.table.email'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.email ?? r.email_masked ?? '—'}
        </span>
      ),
      sortBy: (r) => r.email ?? r.email_masked ?? '',
    },
    {
      id: 'locale',
      header: t('subscribers.table.locale'),
      cell: (r) => (r.locale ? <Badge tone="neutral">{r.locale.toUpperCase()}</Badge> : '—'),
      sortBy: (r) => r.locale ?? '',
    },
    {
      id: 'cohorts',
      header: t('subscribers.table.cohorts'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.cohorts ?? []).map((c) => (
            <Badge key={c} tone="neutral">
              {c}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'joined',
      header: t('subscribers.table.joined'),
      cell: (r) => <DateTime value={r.created_at ?? null} />,
      sortBy: (r) => (r.created_at ? new Date(r.created_at) : null),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('subscribers.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">
            {t('subscribers.subtitle')}
          </p>
        </div>
        <Button variant="ghost" onClick={() => void navigate({ to: '/newsletter/issues' })}>
          {t('issues.title')}
        </Button>
      </header>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-3">
          <div>
            <Label htmlFor="newsletter-locale">{t('subscribers.filters.locale')}</Label>
            <Input
              id="newsletter-locale"
              dir="ltr"
              value={search.locale ?? ''}
              onChange={(e) => setSearchPatch({ locale: e.target.value || undefined })}
              placeholder="en"
            />
          </div>
          <div>
            <Label htmlFor="newsletter-cohort">{t('subscribers.filters.cohort')}</Label>
            <Input
              id="newsletter-cohort"
              dir="ltr"
              value={search.cohort ?? ''}
              onChange={(e) => setSearchPatch({ cohort: e.target.value || undefined })}
              placeholder="founding-100"
            />
          </div>
          <div>
            <Label htmlFor="newsletter-limit">{t('subscribers.filters.limit')}</Label>
            <select
              id="newsletter-limit"
              dir="ltr"
              value={String(filters.limit ?? 25)}
              onChange={(e) => setSearchPatch({ limit: Number(e.target.value) })}
              className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={reveal}
            onChange={(e) =>
              setSearchPatch({ reveal: e.target.checked ? '1' : undefined })
            }
          />
          {reveal ? <Eye className="size-4" aria-hidden /> : <EyeOff className="size-4" aria-hidden />}
          {t('subscribers.reveal.label')}
        </label>
        <AuditChip />
      </div>

      {reveal ? (
        <div
          role="status"
          className="rounded-[var(--radius-md)] border border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)] p-3 text-xs"
        >
          {t('subscribers.reveal.banner')}
        </div>
      ) : null}

      <DataTable<NewsletterSubscriber>
        data={list.data ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        error={list.error ? resolveError(list.error) : null}
        empty={{ title: t('subscribers.empty'), body: t('subscribers.emptyBody') }}
      />
    </div>
  );
}
